import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { CreateClientRequest, ExtraPackagesData, InitialSpecialPackageItemInfo, Person } from "../../types/client";
import { generateRandomNumber } from "../../utils/generateRandomNumber";
import { TypesSortOption } from "../../data/data-products";
import { ClientData } from "./clientResponse";
import { Chapels, GetSpecialPackageProducts, SelectedSpecialPackage } from "./clientInterface";
import { initialClientForm } from "./clientInitialState";

export interface DefaultPackage {
    id: number | null;
    name: string;
}

export interface Default {
    id: number;
    name: string;
}

export type PackageDateTimeField = | 'startDate' | 'startTime' | 'endDate' | 'endTime' | 'downpayment' | 'receiptDate' | 'receiptId';
export type ClearUpdate = 'clientInfo' | 'chapel' | 'addContactPer' | 'extraPack'
export type UpdateClientType = null | 'upSchedule' | 'upChapel' | 'upAdditionalInformation' | 'upAddPackage'
export type UpdateExtraPackageType = "startDate" | "startTime" | "endDate" | "endTime"
export type ActionMutationType = null | 'generatePIN' | 'deleteClient'
export type BooleanType = 'showPin' | 'openOptionStatus' 
export type showClientType = null | 'showUpdateProducts' | 'showUpdatePackageSchedule' | 'showPackageProduct'

interface ClientState {
    selectedClient: ClientData | null;
    qrCode: string | null;
    newClientForm: CreateClientRequest;
    specialPackageTabsType: 'items' | 'product',
    setSelectedPackage: SelectedSpecialPackage | null
    setDefaultPackage: DefaultPackage | null,
    populateSpecialPackageItem: GetSpecialPackageProducts[] | null,
    allPackageFromApi: ExtraPackagesData[] | null
    selectedPackageToUpdate: ExtraPackagesData | null;
    setTypeOfUpdate: UpdateClientType | null;
    selectedPackageView: Default | null;
    selectedClientStatus: TypesSortOption;
    isOpenOptionStatus: boolean;
    isGenerateNewPINOrDelete: ActionMutationType;
    showPin: boolean;
    typeOfShowClient: showClientType
}

const initialState: ClientState  = {
    selectedClient: null,
    qrCode: null,
    newClientForm: initialClientForm,
    specialPackageTabsType: 'items',
    setSelectedPackage: null,
    setDefaultPackage: null,
    populateSpecialPackageItem: null,
    allPackageFromApi: null,
    selectedPackageToUpdate: null,
    setTypeOfUpdate: null,
    selectedPackageView: null,
    selectedClientStatus: { id: 2, label: "Active" },
    isOpenOptionStatus: false,
    isGenerateNewPINOrDelete: null,
    showPin: false,
    typeOfShowClient: null
};

const clientSlice = createSlice({
    name: 'client',
    initialState,
    reducers: {
        setTypeOfShowClient: (state, action: PayloadAction<showClientType>) => {
            state.typeOfShowClient = action.payload;
        },
        setClearTypeOfShowClient: (state) => {
            state.typeOfShowClient = null
        },
        setIsGenerateNewPINOrDelete: (state, action: PayloadAction<ActionMutationType>) => {
            state.isGenerateNewPINOrDelete = action.payload;
        },
        setIsBooleanType: (state, action: PayloadAction<{type: BooleanType, value: boolean}>) => {
            const { type, value } = action.payload;
            if(type === 'showPin') {
                state.showPin = value;
            } else {
                state.isOpenOptionStatus = value;
            }
        },
        resetIsBooleAndPINDelete: (state) => {
            state.isOpenOptionStatus = false;
            state.showPin = false;
            state.isGenerateNewPINOrDelete = null;
        },
        setSelectedClientStatus: (state, action: PayloadAction<TypesSortOption>) => {
            state.selectedClientStatus = action.payload;
        },

        setSelectedPackageView: (state, action: PayloadAction<Default | null>) => {
            state.selectedPackageView = action.payload;
        },
        clearSelectedPackageView: (state) => {
            state.selectedPackageView = null;
        },

        setTypeOfUpdate: (state, action: PayloadAction<UpdateClientType>) => {
            state.setTypeOfUpdate = action.payload;
        },
        clearTypeOfUpdate: (state) => {
            state.setTypeOfUpdate = null;
        },

        setAllPackageFromApi: (state, action: PayloadAction<ExtraPackagesData[]>) => {
            state.allPackageFromApi = action.payload;
        },
        setClearAllPackageFromApi: (state) => {
            state.allPackageFromApi = null
        }, 
        setSelectedPackageToUpdate: ( state, action: PayloadAction<ExtraPackagesData | null> ) => {
            state.selectedPackageToUpdate = action.payload;
        },
        clearSelectedPackageToUpdate: (state) => {
            state.selectedPackageToUpdate = null
        }, 
        updatePackageFromApi: (
            state,
            action: PayloadAction<{
                localId: string;
                field: PackageDateTimeField;
                value: string | number;
            }>
        ) => {
            if (!state.selectedPackageToUpdate) return;

            if (state.selectedPackageToUpdate.localId !== action.payload.localId) return;

            const { field, value } = action.payload;

            if (field === "downpayment") {
                state.selectedPackageToUpdate.downpayment = Number(value);
            } else {
                state.selectedPackageToUpdate[field] = value as string;
            }
        },

        setPopulateSpecialPackageItem: (state, action: PayloadAction<GetSpecialPackageProducts[]>) => {
            state.populateSpecialPackageItem = action.payload;
        },
        togglePopulateSpecialPackageItem: ( state, action: PayloadAction<GetSpecialPackageProducts> ) => {
            const index = state.populateSpecialPackageItem?.findIndex(
                item => item.productID === action.payload.productID
            );

            if (index !== undefined && index !== -1) {
                state.populateSpecialPackageItem?.splice(index, 1);
            } else {
                state.populateSpecialPackageItem?.push(action.payload);
            }
        },
        addSelectedPopulateSpecialPackageItems: ( state, action: PayloadAction<GetSpecialPackageProducts[]> ) => {
            action.payload.forEach(product => {
                const exists = state.populateSpecialPackageItem?.some(
                    item => item.productID === product.productID
                );

                if (!exists) {
                    state.populateSpecialPackageItem?.push(product);
                }
            });
        },
        removeSelectedPopulateSpecialPackageItems: ( state, action: PayloadAction<GetSpecialPackageProducts[]> ) => {
            if (!state.populateSpecialPackageItem) return;

            const idsToRemove = new Set(
                action.payload.map(product => product.productID)
            );

            state.populateSpecialPackageItem =
                state.populateSpecialPackageItem.filter(
                    item => !idsToRemove.has(item.productID)
                );
        },
        clearPopulateSpecialPackageItem: (state) => {
            state.populateSpecialPackageItem = null;
        },

        setDefaultPackage: (state, action: PayloadAction<DefaultPackage>) => {
            state.setDefaultPackage = action.payload
        },
        clearDefaultPackage: (state) => {
            state.setDefaultPackage = { id: null, name: '' }
        },

        setSelectedPackage: (state, action: PayloadAction<SelectedSpecialPackage>) => {
            state.setSelectedPackage = action.payload;
        },
        updateSelectedPackageField: ( state, action: PayloadAction<{ field: keyof SelectedSpecialPackage; value: string | number | boolean; }>) => {
            if (!state.setSelectedPackage) return;

            state.setSelectedPackage[action.payload.field] = action.payload.value as never;
        },
        setClearSelectedPackage: (state) => {
            state.setSelectedPackage = null
        },

        setSpecialPackageTab: (state, action: PayloadAction<'items' | 'product'>) => {
            state.specialPackageTabsType = action.payload;
        },
        resetSpecialPackageTab: (state) => {
            state.specialPackageTabsType = 'items'
        },

        setSelectedClient: (state, action: PayloadAction<ClientData | null>) => {
            state.selectedClient = action.payload;
        },
        clearSelectedClient: (state) => {
            state.selectedClient = null;
        },
        setQrCode: (state, action: PayloadAction<string | null>) => {
            state.qrCode = action.payload;
        },
        clearQrCode: (state) => {
            state.qrCode = null;
        },

        updateClientFormField: < K extends keyof CreateClientRequest >( state: ClientState, action: PayloadAction<{ field: K; value: CreateClientRequest[K]; }> ) => {
            state.newClientForm[action.payload.field] = action.payload.value;
        },
        updateContactPerson: (
            state,
            action: PayloadAction<{
                contactID: number;
                field: "name" | "number";
                value: string;
            }>
        ) => {
            const contact = state.newClientForm.contactPersons.find(
                (c) => c.contactID === action.payload.contactID
            );

            if (!contact) return;

            contact[action.payload.field] = action.payload.value;
        },
        addContactPerson: (state) => {
            state.newClientForm.contactPersons.push({
                contactID: generateRandomNumber(),
                name: "",
                number: "09"
            });
        },
        setContactPersonFromApi: (state, action: PayloadAction<Person[]>) => {
            state.newClientForm.contactPersons = action.payload;
        },
        removeContactPerson: ( state, action: PayloadAction<number>) => {
            state.newClientForm.contactPersons =
                state.newClientForm.contactPersons.filter(
                    (c) => c.contactID !== action.payload
                );
        },
        updateExtraPackage: ( state, action: PayloadAction<{ index: number; field: UpdateExtraPackageType; value: string;}> ) => {
            const { index, field, value } = action.payload;
            state.newClientForm.extraPackages[index][field] = value;
        },
        addChapel: ( state, action: PayloadAction<{ chapelID: number; chapelName: string }>) => {
            
            const chapels = state.newClientForm.chapels ?? [];

            const index = chapels.findIndex(
                (c) => c.chapelID === action.payload.chapelID
            );

            if (index !== -1) {
                chapels.splice(index, 1);
            } else {
                chapels.push(action.payload);
            }

            state.newClientForm.chapels = chapels;
        },
        setChapel: (state, action: PayloadAction<Chapels[]>) => {
            state.newClientForm.chapels = action.payload;
        },
        resetChapel: (state) => {
            state.newClientForm.chapels = [];
        },
        setClientFormFromApi: (state, action: PayloadAction<CreateClientRequest>) => {
            state.newClientForm = action.payload;
        },
        resetClientForm: (state) => {
            state.newClientForm = initialClientForm;
        },

        addPackage: ( 
            state, 
            action: PayloadAction<{ 
                packageId: number;
                packageName: string;
                totalValue: number;
                quantity: number;
            }>) => {
            
            const packages = state.newClientForm.extraPackages ?? [];

            const index = packages.findIndex(
                (c) => c.packageId === action.payload.packageId
            );

            if (index !== -1) {
                packages.splice(index, 1);
            } else {
                packages.push({
                    packageId: action.payload.packageId,
                    packageName: action.payload.packageName,
                    totalValue: action.payload.totalValue,
                    quantity: action.payload.quantity,
                    remainingQty: action.payload.quantity,

                    // ✅ auto assign from client form
                    startDate: state.newClientForm.scheduleFrom,
                    startTime: state.newClientForm.startTime,

                    // defaults
                    endDate: "",
                    endTime: "",
                    packageType: null,
                    products: [],
                    clientPackageId: 0,
                });
            }

            state.newClientForm.extraPackages = packages;
        },
        setPackageFromApi: (state, action: PayloadAction<ExtraPackagesData[]>) => {
            state.newClientForm.extraPackages = action.payload;
        },
        removePackage: (
            state,
            action: PayloadAction<number> 
        ) => {
            state.newClientForm.extraPackages =
                state.newClientForm.extraPackages.filter(
                    (c) => c.packageId !== action.payload
                );
        },
        updatePackage: (
            state,
            action: PayloadAction<{
                packageId: number;
                field: "startDate" | "startTime" | "endDate" | "endTime";
                value: string;
            }>
        ) => {
            const packages = state.newClientForm.extraPackages.find(
                (c) => c.packageId === action.payload.packageId
            );

            if (!packages) return;

            packages[action.payload.field] = action.payload.value;
        },

        setAddItemToSpecialPackage: (state, action: PayloadAction<InitialSpecialPackageItemInfo>) => {
            if (!state.newClientForm.specialPackage) return;

            const items = state.newClientForm.specialPackage.items;

            const index = items.findIndex(
                item => item.id === action.payload.id
            );

            if (index !== -1) {
                // Item exists, remove it
                items.splice(index, 1);
            } else {
                // Item doesn't exist, add it
                items.push(action.payload);
            }
        },
        // setSpecialPackageItems: (state, action: PayloadAction<InitialSpecialPackageItemInfo[]>) => {
        //     if (!state.newClientForm.specialPackage) return;
        //     state.newClientForm.specialPackage.items = action.payload;
        // },
        removeItemFromSpecialPackage: ( state, action: PayloadAction<number>) => {
            if (!state.newClientForm.specialPackage) return;

            state.newClientForm.specialPackage.items =
                state.newClientForm.specialPackage.items.filter(
                item => item.id !== action.payload
                );
        },
        clearSpecialPackageItems: (state) => {
            if (!state.newClientForm.specialPackage) return;
            state.newClientForm.specialPackage.items = [];
        },
        clearUpdateClientInfo: (state, action: PayloadAction<ClearUpdate>) => {
            switch (action.payload) {
                case "extraPack":
                    state.newClientForm.extraPackages = [];
                    break;

                case "clientInfo":
                    state.newClientForm.deceasedName = "";
                    state.newClientForm.registeredBy = "";
                    state.newClientForm.scheduleFrom = "";
                    state.newClientForm.scheduleTo = "";
                    state.newClientForm.startTime = "10:00";
                    state.newClientForm.endTime = "";
                    break;

                case "chapel":
                    state.newClientForm.chapels = [];
                    break;

                case "addContactPer":
                    state.newClientForm.contactPersons = [
                        {
                            contactID: generateRandomNumber(),
                            name: "",
                            number: "09",
                        },
                    ];
                    break;
            }
        },

        resetClientState: () => initialState,
    },
});

export const { 
    setTypeOfShowClient,
    setClearTypeOfShowClient,

    setAllPackageFromApi,
    setClearAllPackageFromApi,
    setSelectedPackageToUpdate,
    clearSelectedPackageToUpdate,
    updatePackageFromApi,

    setSelectedPackageView,
    clearSelectedPackageView,

    setTypeOfUpdate,
    clearTypeOfUpdate,

    setSelectedClient, 
    clearSelectedClient,
    setQrCode,
    clearQrCode,

    updateClientFormField,
    updateContactPerson,
    addContactPerson,
    setContactPersonFromApi,
    removeContactPerson,
    updateExtraPackage,
    setPackageFromApi,
    addChapel,
    setChapel,
    resetChapel,
    setClientFormFromApi,

    addPackage,
    removePackage,
    updatePackage,

    setAddItemToSpecialPackage,
    removeItemFromSpecialPackage,
    clearSpecialPackageItems,

    setSpecialPackageTab,
    resetSpecialPackageTab,

    setSelectedPackage,
    updateSelectedPackageField,
    setClearSelectedPackage,

    setDefaultPackage,
    clearDefaultPackage,

    setPopulateSpecialPackageItem,
    togglePopulateSpecialPackageItem,
    addSelectedPopulateSpecialPackageItems,
    removeSelectedPopulateSpecialPackageItems,
    clearPopulateSpecialPackageItem,

    clearUpdateClientInfo,

    setSelectedClientStatus,
    setIsBooleanType,

    setIsGenerateNewPINOrDelete,
    resetIsBooleAndPINDelete,

    resetClientForm,
    resetClientState,
} = clientSlice.actions;

export default clientSlice.reducer;

export const selectCurrentClient = (state: RootState) => state.client.selectedClient;
export const selectCurrentClientId = (state: RootState) => state.client.selectedClient?.clientID;
export const selectClientQrCode = (state: RootState) => state.client.qrCode;
export const selectClientData = (state: RootState) => state.client.newClientForm;
export const selectClientDataChapels = (state: RootState) => state.client.newClientForm.chapels;
export const selectClientDataContactPerson = (state: RootState) => state.client.newClientForm.contactPersons;
export const selectClientExtraPackage = (state: RootState) => state.client.newClientForm.extraPackages;
export const selectClientSpecialPackageItems = (state: RootState) => state.client.newClientForm.specialPackage?.items;
export const selectSpecialPackageTabs = (state: RootState) => state.client.specialPackageTabsType;
export const selectSelectedSpecialPackage = (state: RootState) => state.client.setSelectedPackage;
export const selectSelectedSpecialPackageId = (state: RootState) => state.client.setSelectedPackage?.id;

export const selectDefaultPackage = (state: RootState) => state.client.setDefaultPackage;
export const selectPopulatePackageItem = (state: RootState) => state.client.populateSpecialPackageItem;

export const selectPackageFromApi = (state: RootState) => state.client.allPackageFromApi;
export const selectUpdatePackage = (state: RootState) => state.client.selectedPackageToUpdate;
export const selectUpdateClientPackageId = (state: RootState) => state.client.selectedPackageToUpdate?.clientPackageId;
export const selectUpdatePackageId = (state: RootState) => state.client.selectedPackageToUpdate?.packageId;
export const selectUpdatePackageLocalId = (state: RootState) => state.client.selectedPackageToUpdate?.localId;
export const selectUpdatePackageType = (state: RootState) => state.client.selectedPackageToUpdate?.packageType;
export const selectTypeOfUpdate = (state: RootState) => state.client.setTypeOfUpdate;

export const selectPackageView = (state: RootState) => state.client.selectedPackageView;
export const selectPackageViewId = (state: RootState) => state.client.selectedPackageView?.id;

export const selectedClientStatus = (state: RootState) => state.client.selectedClientStatus;
export const selectedClientStatusLabel = (state: RootState) => state.client.selectedClientStatus.label;
export const selectedClientStatusId = (state: RootState) => state.client.selectedClientStatus.id;

export const selectedIsOpenOptionStatus = (state: RootState) => state.client.isOpenOptionStatus;
export const selectedIsGenerateNewPINOrDelete = (state: RootState) => state.client.isGenerateNewPINOrDelete;
export const selectedIsShowPin = (state: RootState) => state.client.showPin;

export const selectTypeOfShowClient = (state: RootState) => state.client.typeOfShowClient;
